import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Star, MessageCircle } from "lucide-react";
import { business, heroImage, waLink } from "@/config/business";
import { Overline } from "@/components/Reveal";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ onBook }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden bg-cream" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-14 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          >
            <Overline>Paldi Meena • Jaipur</Overline>
          </motion.div>

          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl xl:text-[5.5rem] leading-[1.05] tracking-tight text-ink mt-6">
            <MaskedLine delay={0.25}>Feel Good.</MaskedLine>
            <MaskedLine delay={0.4} className="italic text-primary">
              Look Your Best.
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-muted-foreground"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
          >
            Hair, beauty and grooming services in Paldi Meena, Jaipur — for everyday
            self-care, special occasions and everything in between.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
          >
            <button
              data-testid="hero-book-button"
              onClick={onBook}
              className="bg-primary text-primary-foreground px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-deep transition-colors min-h-[48px]"
            >
              Book an Appointment
            </button>
            <a
              data-testid="hero-whatsapp-button"
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-wa text-wa px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-wa hover:text-white transition-colors min-h-[48px]"
            >
              <MessageCircle size={17} /> WhatsApp Us
            </a>
          </motion.div>

          <motion.div
            data-testid="hero-trust-line"
            className="mt-8 flex items-center gap-2 text-sm text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <Star size={15} className="fill-accent text-accent" aria-hidden="true" />
            <span>
              <strong className="text-ink font-semibold">{business.rating} ★</strong> on Google •{" "}
              {business.reviewCount} reviews
            </span>
            <span className="text-[10px] uppercase tracking-widest border border-accent/60 text-accent px-2 py-0.5 ml-1">
              {business.verifyLabel}
            </span>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
          >
            <div className="absolute -inset-3 border border-accent/50 pointer-events-none" aria-hidden="true" />
            <div className="overflow-hidden">
              <motion.img
                src={heroImage.src}
                alt={heroImage.alt}
                style={{ y: imgY }}
                className="w-full h-[420px] md:h-[560px] object-cover scale-110"
                loading="eager"
                fetchpriority="high"
                data-testid="hero-image"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-deep text-cream px-6 py-4 hidden md:block">
              <p className="font-display italic text-lg">Beauty, done personally.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
